/*
  x-video-autoplay.js
  ---------------------------------------------------------------------------
  Makes product videos rendered through Dawn's <deferred-media> component start
  on their own, stay muted, play inline and loop forever — on desktop and on
  mobile (iOS Safari / Android Chrome included), with no tap required.

  Why this file exists:
  Dawn keeps the <video> inside a <template> and only injects it when the user
  clicks the play poster. Its DeferredMedia.loadContent() also calls the global
  window.pauseAllMedia(), which pauses EVERY video on the page — so with two
  videos on one page they cancelled each other out. This controller injects the
  markup up front and re-starts any managed video that another theme component
  pauses behind its back.

  Markup contract: <deferred-media data-x-autoplay> (see x-product-thumbnail).
*/
(function () {
  if (window.__xVideoAutoplay) return;
  window.__xVideoAutoplay = true;

  var SELECTOR = 'deferred-media[data-x-autoplay]';
  var MAX_RESUMES = 30; // guards against a play()/pause() ping-pong loop

  function modalIsOpen() {
    return !!document.querySelector('product-media-modal[open], .product-media-modal[open]');
  }

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var r = el.getBoundingClientRect();
    if (!r.width && !r.height) return false;
    return r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
  }

  // Enforce the attribute set mobile browsers require for silent autoplay.
  function prepare(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('disablepictureinpicture', '');
    video.removeAttribute('controls');
  }

  function play(video) {
    if (!video || !video.isConnected) return;
    prepare(video);
    var p;
    try {
      p = video.play();
    } catch (e) {
      return;
    }
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  // Same job as Dawn's DeferredMedia.loadContent(), minus its call to the
  // global window.pauseAllMedia() — that call is what made two videos on one
  // page stop each other as they initialised.
  function inject(el) {
    var existing = el.querySelector('video');
    if (existing) return existing;

    var tpl = el.querySelector('template');
    if (!tpl || !tpl.content || !tpl.content.firstElementChild) return null;

    var holder = document.createElement('div');
    holder.appendChild(tpl.content.firstElementChild.cloneNode(true));
    var node = holder.querySelector('video, iframe');
    if (!node) return null;

    el.setAttribute('loaded', true); // Dawn's CSS hides the poster on [loaded]
    el.appendChild(node);
    return node.nodeName === 'VIDEO' ? node : null;
  }

  function manage(el) {
    if (el.__xManaged) return;
    el.__xManaged = true;

    // No "click to play" affordance — the video starts by itself.
    var icon = el.querySelector('.deferred-media__poster-button');
    if (icon) icon.style.display = 'none';

    customElements.whenDefined('deferred-media').then(function () {
      if (!el.isConnected) return;

      // Pull the <video> out of the <template> Dawn parks it in.
      var video = null;
      try {
        video = inject(el);
      } catch (e) {
        /* no-op */
      }

      if (!video) {
        // Unexpected markup, or an external video (YouTube/Vimeo iframe, which
        // autoplays and loops through its own URL parameters). Fall back to the
        // theme's own loader so the media still appears.
        try {
          if (!el.hasAttribute('loaded') && typeof el.loadContent === 'function') el.loadContent(false);
        } catch (e2) {
          /* no-op */
        }
        video = el.querySelector('video');
        if (!video) return;
      }

      prepare(video);
      video.__xResumes = 0;
      video.__xPausedByUs = false;

      ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach(function (evt) {
        video.addEventListener(evt, function () {
          if (!video.__xPausedByUs && isVisible(el)) play(video);
        });
      });

      video.addEventListener('playing', function () {
        video.__xResumes = 0;
      });

      // Safety net: another theme component (a second deferred-media loading,
      // media-gallery variant switching, the lightbox closing) calls the global
      // pauseAllMedia() and stops this video. Start it again.
      video.addEventListener('pause', function () {
        if (video.__xPausedByUs || modalIsOpen()) return;
        if (video.__xResumes++ >= MAX_RESUMES) return;
        setTimeout(function () {
          if (!video.__xPausedByUs && !modalIsOpen() && isVisible(el)) play(video);
        }, 80);
      });

      // Ended should never fire (loop is on) — belt and braces for the
      // "restart from the beginning" requirement.
      video.addEventListener('ended', function () {
        if (video.__xPausedByUs) return;
        try {
          video.currentTime = 0;
        } catch (e) {
          /* no-op */
        }
        play(video);
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                video.__xPausedByUs = false;
                play(video);
              } else {
                video.__xPausedByUs = true;
                try {
                  video.pause();
                } catch (e) {
                  /* no-op */
                }
              }
            });
          },
          { threshold: 0 }
        );
        io.observe(el);
      }

      // Start straight away when it is already on screen. Anything further
      // down the page is started by the observer the moment it scrolls in,
      // which is also what mobile browsers are happiest with.
      if (isVisible(el)) play(video);
    });
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(manage);
  }

  function init() {
    scan(document);

    // Galleries are re-rendered on variant change and in the theme editor.
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.matches && node.matches(SELECTOR)) manage(node);
            else if (node.querySelectorAll) scan(node);
          });
        });
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    // Mobile browsers suspend playback when the tab/app goes to the background.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible' || modalIsOpen()) return;
      document.querySelectorAll(SELECTOR).forEach(function (el) {
        var video = el.querySelector('video');
        if (video && !video.__xPausedByUs) play(video);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
